import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { SECOND_MS } from '@lootboy/lib/time';

import Snackbar, {
  ISnackbarProps,
  ISnackbarRef,
} from '~/components/molecules/Snackbar';

import { useTimeout } from '~/hooks/useTimeout';

import styles from './SnackbarContext.module.scss';

type TPushSnackbarFunc = (snackbarProps: ISnackbarProps) => void;

interface ISnackbarContextState {
  /**
   * Pushes snackbar with animation with provided props to the bottom of the snackbar queue with default timeout
   */
  pushSnackbar: TPushSnackbarFunc;
  /**
   * Hides snackbar and resolves after the animation is finished
   */
  dismissSnackbar: () => Promise<void>;
}

const SnackbarContext = createContext<ISnackbarContextState>({
  pushSnackbar: () => {},
  dismissSnackbar: () => Promise.resolve(),
});

export function useSnackbar(): ISnackbarContextState {
  const { dismissSnackbar, pushSnackbar } = useContext(SnackbarContext);

  return {
    dismissSnackbar,
    pushSnackbar,
  };
}

const SNACKBAR_TIMEOUT = 6 * SECOND_MS;

export function SnackbarContextProvider({
  children,
}: PropsWithChildren<never>): JSX.Element {
  const [snackbar, setSnackbar] = useState<ISnackbarProps | null>(null);
  const { timeout, clear } = useTimeout();
  const snackbarElementRef = useRef<ISnackbarRef>(null);

  const snackbarContextValue = useMemo(() => {
    const dismissSnackbar = async () => {
      if (!snackbarElementRef.current) return;
      await snackbarElementRef.current.animateOut();
      setSnackbar(null);
      clear();
    };

    const pushSnackbar = async (snackbarProps: ISnackbarProps) => {
      if (snackbarElementRef.current) {
        await dismissSnackbar();
      }
      setSnackbar(snackbarProps);
      timeout(SNACKBAR_TIMEOUT, () => {
        dismissSnackbar();
      });
    };

    return {
      dismissSnackbar,
      pushSnackbar,
    } as const;
  }, [clear, timeout]);

  return (
    <SnackbarContext.Provider value={snackbarContextValue}>
      {children}
      {ReactDOM.createPortal(
        <div className={styles.snackbarContainer}>
          {snackbar ? (
            <Snackbar
              variant={snackbar.variant}
              ref={snackbarElementRef}
              onClose={snackbar.onClose}
            >
              {snackbar.children ?? null}
            </Snackbar>
          ) : null}
        </div>,
        document.body,
      )}
    </SnackbarContext.Provider>
  );
}
